// Feed watchdog snapshot (server pkg/feedwatch): every paying user's Walmart
// and Home Depot feed, refreshed every 10 minutes on the monitoring host.

import FetchClient, { type ApiResponse } from "./FetchClient";

export type FeedStatus = "ok" | "thin" | "empty" | "partial" | "new-waiting";

export interface FeedRepair {
  retailer: string;
  storeId: string;
  reason: string;
  requestedAt: number;
  startedAt?: number;
  finishedAt?: number;
  host?: string;
  ok: boolean;
  error?: string;
  attemptsToday: number;
}

export interface FeedWindowOutcome {
  window: string;
  categories: number;
  complete: number;
  exhausted: number;
  items: number;
}

export interface FeedUserRow {
  userId: string;
  plans: string[];
  status: FeedStatus;
  visible: number;
  baseline: number;
  stores: string[];
  emptyStores?: string[];
  thinStores?: string[];
  createdAt: number;
  lastActive?: number;
  reason?: string;
  new?: boolean;
}

export interface FeedStoreRow {
  storeId: string;
  users: number;
  status: FeedStatus;
  visible: number;
  baseline: number;
  newToday: number;
  newestAt: number;
  window?: FeedWindowOutcome;
  repair?: FeedRepair;
}

export interface FeedWindowSummary {
  window: string;
  stores: number;
  ok: number;
  partial: number;
  zero: number;
}

export interface FeedNewSubscriber {
  userId: string;
  stores: string[];
  entitledAt: number;
  firstProductsAt?: number;
  firstPushAt?: number;
  pushNote?: string;
  visible: number;
}

export interface FeedRetailerSnapshot {
  retailer: "walmart" | "home_depot";
  users: number;
  stores: number;
  emptyUsers: number;
  partialUsers: number;
  thinUsers: number;
  emptyStores: number;
  thinStores: number;
  medianUserFeed: number;
  repairQueued: number;
  repairOldestMin: number;
  windows?: FeedWindowSummary[];
  userRows: FeedUserRow[];
  storeRows: FeedStoreRow[];
  newSubscribers: FeedNewSubscriber[] | null;
}

export interface FeedHealthData {
  snapshot: {
    generatedAt: number;
    fullPassAt: number;
    durationMs: number;
    retailers: FeedRetailerSnapshot[];
    events: string[] | null;
  };
  ageSeconds: number;
  stale: boolean;
}

export async function getFeedHealth(): Promise<FeedHealthData> {
  const response = await FetchClient.get<ApiResponse<FeedHealthData>>("/admin/feed-health");
  if (!response?.data) throw new Error("Feed watchdog returned no data");
  return response.data;
}

export async function queueFeedRepair(retailer: string, storeId: string): Promise<{ queued: boolean; reason: string }> {
  const response = await FetchClient.post<ApiResponse<{ queued: boolean; reason: string }>>("/admin/feed-health/repair", { retailer, storeId });
  if (!response?.data) throw new Error("Repair request failed");
  return response.data;
}
